import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './SelectMenuEnhancer.css';

const OPEN_KEYS = new Set(['Enter', ' ', 'ArrowDown', 'ArrowUp']);

function selectableIndex(options, start, direction) {
  if (!options.length) return -1;
  for (let offset = 1; offset <= options.length; offset += 1) {
    const index = (start + direction * offset + options.length) % options.length;
    if (!options[index].disabled) return index;
  }
  return -1;
}

function edgeIndex(options, fromEnd = false) {
  const indexes = options.map((_option, index) => index);
  if (fromEnd) indexes.reverse();
  return indexes.find(index => !options[index].disabled) ?? -1;
}

function optionSnapshot(select) {
  return Array.from(select.options).map((option, index) => ({
    index,
    value: option.value,
    label: option.textContent?.trim() || option.value,
    disabled: option.disabled || option.parentElement?.disabled || false,
    group: option.parentElement?.tagName === 'OPTGROUP'
      ? option.parentElement.label
      : '',
  }));
}

function menuPosition(select, optionCount) {
  const rect = select.getBoundingClientRect();
  const gutter = 8;
  const gap = 6;
  const width = Math.min(Math.max(rect.width, 180), Math.min(420, window.innerWidth - gutter * 2));
  const left = Math.min(Math.max(rect.left, gutter), window.innerWidth - width - gutter);
  const estimatedHeight = Math.min(optionCount * 40 + 12, 320);
  const roomBelow = window.innerHeight - rect.bottom - gap - gutter;
  const roomAbove = rect.top - gap - gutter;
  const placeAbove = roomBelow < Math.min(estimatedHeight, 180) && roomAbove > roomBelow;
  return {
    placement: placeAbove ? 'top' : 'bottom',
    left,
    width,
    maxHeight: Math.max(96, Math.min(320, placeAbove ? roomAbove : roomBelow)),
    ...(placeAbove
      ? { bottom: window.innerHeight - rect.top + gap }
      : { top: rect.bottom + gap }),
  };
}

function selectLabel(select) {
  const labelText = Array.from(select.labels || []).map(label => {
    const copy = label.cloneNode(true);
    copy.querySelectorAll('select, input, textarea, button').forEach(control => control.remove());
    return copy.textContent?.trim();
  }).find(Boolean);
  return select.getAttribute('aria-label')
    || labelText
    || select.name
    || 'Options';
}

export default function SelectMenuEnhancer() {
  const generatedId = useId().replace(/:/g, '');
  const listboxId = `sayable-select-menu-${generatedId}`;
  const [menu, setMenu] = useState(null);
  const menuStateRef = useRef(null);
  const menuElementRef = useRef(null);
  const typeaheadRef = useRef({ value: '', timer: null });
  const selectAttributesRef = useRef(new WeakMap());

  function restoreSelect(select) {
    if (!select) return;
    const previousAttributes = selectAttributesRef.current.get(select) || {};
    ['aria-controls', 'aria-expanded', 'aria-activedescendant'].forEach(attribute => {
      const previous = previousAttributes[attribute];
      if (previous === null || previous === undefined) select.removeAttribute(attribute);
      else select.setAttribute(attribute, previous);
    });
    selectAttributesRef.current.delete(select);
  }

  function closeMenu({ returnFocus = false } = {}) {
    const current = menuStateRef.current;
    if (!current) return;
    restoreSelect(current.select);
    menuStateRef.current = null;
    setMenu(null);
    if (typeaheadRef.current.timer) window.clearTimeout(typeaheadRef.current.timer);
    typeaheadRef.current = { value: '', timer: null };
    if (returnFocus && current.select.isConnected) {
      window.requestAnimationFrame(() => current.select.focus({ preventScroll: true }));
    }
  }

  function exposeSelectState(select, activeIndex) {
    selectAttributesRef.current.set(select, Object.fromEntries(
      ['aria-controls', 'aria-expanded', 'aria-activedescendant'].map(attribute => [
        attribute,
        select.getAttribute(attribute),
      ]),
    ));
    select.setAttribute('aria-controls', listboxId);
    select.setAttribute('aria-expanded', 'true');
    if (activeIndex >= 0) select.setAttribute('aria-activedescendant', `${listboxId}-option-${activeIndex}`);
  }

  function openMenu(select) {
    if (!select || select.disabled || select.multiple || Number(select.size || 0) > 1) return;
    if (menuStateRef.current?.select === select) return;
    closeMenu();
    const options = optionSnapshot(select);
    const selectedIndex = options.findIndex(option => option.index === select.selectedIndex && !option.disabled);
    const activeIndex = selectedIndex >= 0 ? selectedIndex : edgeIndex(options);
    const nextMenu = {
      select,
      options,
      activeIndex,
      selectedValue: select.value,
      label: selectLabel(select),
      position: menuPosition(select, options.length),
    };
    exposeSelectState(select, activeIndex);
    menuStateRef.current = nextMenu;
    setMenu(nextMenu);
  }

  function updateActiveIndex(index) {
    const current = menuStateRef.current;
    if (!current || index < 0 || current.options[index]?.disabled) return;
    current.select.setAttribute('aria-activedescendant', `${listboxId}-option-${index}`);
    const nextMenu = { ...current, activeIndex: index };
    menuStateRef.current = nextMenu;
    setMenu(nextMenu);
    window.requestAnimationFrame(() => {
      document.getElementById(`${listboxId}-option-${index}`)?.scrollIntoView({ block: 'nearest' });
    });
  }

  function chooseOption(index) {
    const current = menuStateRef.current;
    const option = current?.options[index];
    if (!current || !option || option.disabled) return;
    const changed = current.select.value !== option.value;
    current.select.value = option.value;
    if (changed) current.select.dispatchEvent(new Event('change', { bubbles: true }));
    closeMenu({ returnFocus: true });
  }

  useEffect(() => {
    function selectFromTarget(target) {
      return target?.tagName === 'SELECT' ? target : null;
    }

    function handlePointerDown(event) {
      const select = selectFromTarget(event.target);
      if (select && !select.disabled && !select.multiple && Number(select.size || 0) <= 1) {
        event.preventDefault();
        select.focus({ preventScroll: true });
        openMenu(select);
        return;
      }
      if (menuStateRef.current
        && !menuElementRef.current?.contains(event.target)
        && event.target !== menuStateRef.current.select) closeMenu();
    }

    function handleClick(event) {
      const select = selectFromTarget(event.target);
      if (!select || select.disabled || select.multiple || Number(select.size || 0) > 1) return;
      event.preventDefault();
      if (menuStateRef.current?.select !== select) {
        select.focus({ preventScroll: true });
        openMenu(select);
      }
    }

    function handleKeyDown(event) {
      const select = selectFromTarget(event.target);
      const current = menuStateRef.current;
      if (!select) return;
      if (!current || current.select !== select) {
        if (OPEN_KEYS.has(event.key)) {
          event.preventDefault();
          openMenu(select);
        }
        return;
      }
      if (event.key === 'Tab') {
        closeMenu();
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu({ returnFocus: true });
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        chooseOption(current.activeIndex);
        return;
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        updateActiveIndex(selectableIndex(current.options, current.activeIndex, event.key === 'ArrowDown' ? 1 : -1));
        return;
      }
      if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        updateActiveIndex(edgeIndex(current.options, event.key === 'End'));
        return;
      }
      if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        const search = `${typeaheadRef.current.value}${event.key}`.toLocaleLowerCase();
        if (typeaheadRef.current.timer) window.clearTimeout(typeaheadRef.current.timer);
        typeaheadRef.current = {
          value: search,
          timer: window.setTimeout(() => { typeaheadRef.current.value = ''; }, 650),
        };
        const match = current.options.findIndex(option => !option.disabled && option.label.toLocaleLowerCase().startsWith(search));
        if (match >= 0) updateActiveIndex(match);
      }
    }

    function handleViewportChange(event) {
      if (event.type === 'scroll' && menuElementRef.current?.contains(event.target)) return;
      closeMenu();
    }

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('click', handleClick, true);
    document.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
      closeMenu();
    };
  // These document listeners are installed once; live menu values are read
  // through refs so feature re-renders do not replace global event handlers.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!menu) return null;
  let previousGroup = null;
  return createPortal(
    <div
      ref={menuElementRef}
      id={listboxId}
      className={`sayable-select-menu ${menu.position.placement}`}
      role="listbox"
      aria-label={menu.label}
      style={{
        left: menu.position.left,
        width: menu.position.width,
        maxHeight: menu.position.maxHeight,
        ...(menu.position.top !== undefined ? { top: menu.position.top } : {}),
        ...(menu.position.bottom !== undefined ? { bottom: menu.position.bottom } : {}),
      }}
    >
      {menu.options.map((option, index) => {
        const showGroup = option.group && option.group !== previousGroup;
        previousGroup = option.group;
        const selected = option.value === menu.selectedValue;
        return <React.Fragment key={`${option.value}-${index}`}>
          {showGroup ? <div className="sayable-select-group" role="presentation">{option.group}</div> : null}
          <button
            id={`${listboxId}-option-${index}`}
            className={`${menu.activeIndex === index ? 'active' : ''}${selected ? ' selected' : ''}`}
            type="button"
            role="option"
            aria-selected={selected}
            disabled={option.disabled}
            title={option.label}
            onPointerMove={() => updateActiveIndex(index)}
            onClick={() => chooseOption(index)}
          >
            <span>{option.label}</span><span className="sayable-select-check" aria-hidden="true">{selected ? '✓' : ''}</span>
          </button>
        </React.Fragment>;
      })}
    </div>,
    document.body,
  );
}
